import { Dayjs } from 'dayjs';
import { KeyValueStringMap, MhpDetails } from 'modules/types/common';
import { LabDataSource } from 'web/src/screens/TestResults/Lab/LabTrendLine/enums';

export interface TestResultsListResponse {
  total: number;
  results: TestResult[];
  errors: any[];
}

export const NO_PROVIDER = '(No Provider)';

export enum TestResultsFilterType {
  TEST_TYPE = 'TEST_TYPE',
  PROVIDER = 'PROVIDER'
}

export interface TestResultsFilter {
  label: string;
  value: string;
  checked: boolean;
  type?: TestResultsFilterType;
}

export interface DateRangeFilterParams {
  year: string;
  startDate: Dayjs | string;
  endDate: Dayjs | string;
}

export enum SortByValues {
  desc = 'desc',
  asc = 'asc',
  atoz = 'atoz',
  ztoa = 'ztoa'
}

export const SortByMap = {
  [SortByValues.desc]: 'New',
  [SortByValues.asc]: 'Old',
  [SortByValues.atoz]: 'A to Z',
  [SortByValues.ztoa]: 'Z to A'
};

export interface ProviderLocation {
  locationName: string;
  locationId: string;
  locationNcid: string;
}

export interface ReferringProvider {
  corpProvId: string;
  isSelectHealth?: boolean;
  providerId: string;
  providerImageUrl: string;
  providerLocation: ProviderLocation;
  providerName: string;
  providerNcid: string;
  providerPhoneNumber?: string;
  providerSpecialty: string;
  providerType: string;
  providerTypeNcid: string;
}

export enum TestResultCategory {
  IMAGING = 'Imaging',
  LAB = 'Lab',
  MICROBIOLOGY = 'Microbiology',
  PATHOLOGY = 'Pathology'
}

export interface TestResultFocused {
  adapterSource?: string;
  category: TestResultCategory;
  componentEventIds: string[];
  encounterId: string;
  eventId: string;
  isRead: boolean;
  mhpDetails?: MhpDetails;
  name: string;
  presentedForm?: PresentedForm[];
  providerNames: string[];
  source: LabDataSource;
  testDate: string;
}

export interface TestResult extends TestResultFocused {
  dfdEventId?: string;
  providerLocation: ProviderLocation;
  referringProvider: ReferringProvider;
}

export interface TestResultDetail {
  source: LabDataSource;
  name: string;
  eventId: string;
  testDate: string;
  notes: string | null;
  testLocation: string;
  testLocationNcid: string | null;
  referringProvider: ReferringProvider;
  encounterId: string;
  providerLocation: string | null;
  providerNames: string[] | null;
  specimenSource: string;
  components: LabComponent[];
  performingLab: string;
  performingLabAddress: string;
  performingLabPhone: string;
  patientLetters: any[];
  collectionDate: string;
  title: string;
}

export interface TestResultError {
  requestId: string;
  errorCode: string;
  errorDescription: string;
  errors: string[];
}

export interface PresentedForm {
  featureType?: PresentedFormFeatureType;
  contentType: string;
  url: string;
}

export enum PresentedFormFeatureType {
  DETAIL = 'detail',
  DOWNLOAD = 'download',
  SHARE = 'share'
}

export interface MicrobiologyDetails {
  encounterId: string;
  eventId: string;
  microbiologyDate: string;
  name: string;
  providerName: string;
  providerNcid: string;
  resultList: string[];
  source: LabDataSource;
  stain: boolean;
  stainResult: null | string;
}

export interface MicrobiologyResult {
  eventId: string;
  name: string;
  source: LabDataSource;
  microbiologyDate: string;
  resultList: string[];
  stain: boolean;
  stainResult: null | string;
}

export interface ReferenceSystem {
  value: number;
  unit: string;
  system: string;
}

export interface LabComponent {
  encounterId: string;
  eventId: string;
  interpretation?: string | null;
  issued: string;
  loincCode?: string | null;
  name: string;
  notes?: string | null;
  referenceRange?: {
    high: ReferenceSystem;
    low: ReferenceSystem;
    text: string;
  } | null;
  status: string;
  valueQuantity?: ReferenceSystem | null;
  valueString?: string | null;
}

export interface LabResultsListItems extends LabComponent {
  adapterSource?: string;
  source: LabDataSource;
  panelName: string;
}

export interface LabResult {
  adapterSource?: string;
  components: LabComponent[];
  encounterId: string | null;
  eventId: string | null;
  name: string;
  title?: string;
  notes: string | null;
  source: LabDataSource;
  testDate: string;
  testLocation: string | null;
  testLocationNcid: string | null;
  providerNames: string | null;
  specimenSource?: string | null;
  collectionDate?: string | null;
  performingLab?: string | null;
  performingLabAddress?: string | null;
  performingLabPhone?: string | null;
  patientLetters?: PatientLetters[] | null;
}

export interface PatientLetters {
  title: string;
  docId: string | number;
}

export interface LabResultFull extends LabResult {
  category: TestResultCategory;
  componentEventIds: string[];
  isRead: boolean;
  mhpDetails?: MhpDetails;
  customInformation?: {
    text: string;
    url: string;
  };
}

export interface LabDetails {
  total: number;
  results: LabResult[] | LabResultFull[];
  errors?: any[];
  customInformation?: {
    text: string;
    url: string;
  };
}

export interface PathologyDetails {
  contentType: TestResultContentType;
  content: string;
}

/**
 * Details including extra props from test-result record
 */
export interface MicrobiologyDetailsFull extends MicrobiologyDetails {
  category: TestResultCategory;
  componentEventIds: string[];
  isRead: boolean;
  mhpDetails?: MhpDetails;
  providerNames: string[];
  testDate: string;
}

export interface PathologyDetailsFull extends PathologyDetails {
  category: TestResultCategory;
  componentEventIds: string[];
  encounterId: string;
  eventId: string;
  isRead: boolean;
  mhpDetails?: MhpDetails;
  name: string;
  presentedForm?: PresentedForm[];
  providerNames: string[];
  source: LabDataSource;
  testDate: string;
}

export interface TestResultDetails {
  contentType: TestResultContentType;
  content:
    | ArrayBuffer
    | MicrobiologyDetails
    | MicrobiologyDetailsFull
    | LabDetails
    | LabResultFull[]
    | PathologyDetails
    | PathologyDetailsFull;
}

export const TestResultsFilterCategoryMap: KeyValueStringMap = {
  [TestResultCategory.IMAGING]: 'Imaging and Radiology',
  [TestResultCategory.LAB]: 'Laboratory',
  [TestResultCategory.MICROBIOLOGY]: 'Microbiology',
  [TestResultCategory.PATHOLOGY]: 'Pathology'
};

export enum TestResultContentType {
  HTML = 'text/html',
  PDF = 'application/pdf',
  JSON = 'application/json'
}

/**
 * LAB HISTORICAL TYPES
 */

export interface LabHistoricalComponent extends LabComponent {
  valueString: string | null;
}

export interface LabHistoricalResult extends Omit<LabResult, 'components'> {
  components: LabHistoricalComponent[];
}

export interface LabHistorical {
  total: number;
  results: LabHistoricalResult[];
  errors?: any[];
}

interface ShareResults {
  toAddress: string;
  message?: string;
  subject?: string;
  useSecureEmail: boolean;
  contactPhoneNumber: string;
}

/* eslint-disable @typescript-eslint/no-empty-interface */
export interface ShareLabResults extends ShareResults {}
export interface ShareMicrobiologyResults extends ShareResults {}
export interface ShareImagingResults extends ShareResults {}
export interface SharePathologyResults extends ShareResults {}
/* eslint-enable @typescript-eslint/no-empty-interface */

/**
 * MICROBIOLOGY HISTORICAL TYPES
 */
export interface MicrobiologyHistorical extends MicrobiologyDetails {
  total: number;
  results: MicrobiologyResult[];
  errors?: any[];
}

export interface TestResultsList {
  results: TestResult[];
  total: number;
  errors: any[];
}

export interface TestResultWithDetails {
  testResult: TestResult;
  details: TestResultDetail;
}
